import {isFeatureEnabled} from '@github-ui/feature-flags'
import {TEMP_SUB_ISSUE_ID_PREFIX} from '@github-ui/issue-create/values'
import type {OnDropArgs} from '@github-ui/drag-and-drop'
import {OPTIMISTIC_KEY} from '@github-ui/optimistic-connection'

/** Both flags: the bridge only runs on the TanStack secondary view, so overlay-only stays on Relay. */
export function isSubIssuesOptimisticOverlayEnabled(): boolean {
  return (
    isFeatureEnabled('issue_viewer_subissues_optimistic_overlay') &&
    isFeatureEnabled('issue_viewer_secondary_view_tanstack')
  )
}

/** Whether `id` is the client-only id a pending create row carries until the server assigns one. */
export function isTempSubIssueId(id: string | null | undefined): boolean {
  return id != null && id.startsWith(TEMP_SUB_ISSUE_ID_PREFIX)
}

export type ReprioritizeSubIssueParams = {
  issueId: string
  dragIssueId: string
  dropIssueId: string
  isBefore: boolean
}

/**
 * Resolves a drop into the reprioritise input, or `null` when it must not reach the server: a
 * self-drop, or (overlay only) either endpoint being a temp row with no server id yet.
 */
export function resolveSubIssueReorder(
  parentIssueId: string,
  {dragMetadata, dropMetadata, isBefore}: OnDropArgs<string>,
  overlayEnabled: boolean,
): ReprioritizeSubIssueParams | null {
  if (dragMetadata.id === dropMetadata.id) return null
  if (overlayEnabled && (isTempSubIssueId(dragMetadata.id) || isTempSubIssueId(dropMetadata.id))) return null
  return {issueId: parentIssueId, dragIssueId: dragMetadata.id, dropIssueId: dropMetadata.id, isBefore}
}

/**
 * Whether a row is a just-confirmed optimistic create, so a click during replication lag retries
 * the Not Found instead of erroring. `hadOptimisticUpdate && isLast` is a Relay-path fallback — a
 * one-way latch that would mark the last row forever — so the overlay uses `OPTIMISTIC_KEY` instead.
 */
export function resolveFromOptimisticUpdate(
  issueNode: object,
  {
    overlayEnabled,
    hadOptimisticUpdate,
    isLast,
  }: {overlayEnabled: boolean; hadOptimisticUpdate: boolean; isLast: boolean},
): boolean {
  return OPTIMISTIC_KEY in issueNode || (!overlayEnabled && hadOptimisticUpdate && isLast)
}

/**
 * Moves `dragId` into `dropId`'s slot. Removes first, then indexes against the remaining array, so
 * the result matches the reprioritise mutation's before/after target. Unchanged if either id is absent.
 */
export function reorderNodes<T extends {id: string} | null | undefined>(
  nodes: readonly T[] | null,
  dragId: string,
  dropId: string,
  isBefore: boolean,
): readonly T[] | null {
  if (!nodes) return nodes
  // A self-move would remove the node and then index against its own absence, corrupting the order.
  if (dragId === dropId) return nodes
  const from = nodes.findIndex(node => node?.id === dragId)
  if (from === -1 || nodes.findIndex(node => node?.id === dropId) === -1) return nodes
  const next = [...nodes]
  const moved = next.splice(from, 1)
  const dropIndex = next.findIndex(node => node?.id === dropId)
  next.splice(isBefore ? dropIndex : dropIndex + 1, 0, ...moved)
  return next
}
